import { useEffect, useState } from "react";

// Writes that failed for lack of connectivity, replayed in order once the network is back.
// Every queued write carries a client-generated id, so a replay of a request that did reach
// the server is ignored there instead of being stored twice.
const KEY = "logsite_outbox_v1";
const RETRY_MS = 30000;
const EVENT = "outbox-change";

const read = () => { try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch { return []; } };
const write = items => {
  try { localStorage.setItem(KEY, JSON.stringify(items)); } catch {}
  window.dispatchEvent(new Event(EVENT));
};

// randomUUID is missing before iOS 15.4 and outside HTTPS, so fall back to a v4 UUID built by hand.
export function newRef() {
  const c = window.crypto;
  if (c?.randomUUID) return c.randomUUID();
  const rand = () => (c?.getRandomValues ? c.getRandomValues(new Uint8Array(1))[0] : Math.floor(Math.random() * 256));
  return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, d => (d ^ (rand() & (15 >> (d / 4)))).toString(16));
}

export const pending = () => read();

// Statuses worth retrying: no network, the server or proxy is down, or the session lapsed.
const retryable = status => status === 0 || status === 401 || status === 408 || status === 429 || status >= 500;

async function attempt(item) {
  try {
    const r = await fetch(item.url, {
      method: item.method, credentials: "include",
      headers: item.body ? { "Content-Type": "application/json" } : undefined,
      body: item.body ? JSON.stringify(item.body) : undefined,
    });
    return r.status;
  } catch {
    return 0;
  }
}

// Send now; on failure park it in the outbox. Returns "sent", "queued" or "rejected".
export async function send(url, { method = "POST", body, kind, meta } = {}) {
  const item = { id: newRef(), url, method, body, kind, meta, at: Date.now(), tries: 0 };
  if (read().length === 0) {
    const status = await attempt(item);
    if (status >= 200 && status < 300) return "sent";
    if (!retryable(status)) return "rejected";
  }
  // Anything already waiting must go first, so new writes queue behind it.
  write([...read(), item]);
  return "queued";
}

export function remove(predicate) {
  const items = read();
  const kept = items.filter(i => !predicate(i));
  if (kept.length !== items.length) write(kept);
  return items.length - kept.length;
}

let flushing = null;

export function flush() {
  if (flushing) return flushing;
  flushing = (async () => {
    let lastStatus = null;
    for (;;) {
      const [item] = read();
      if (!item) break;
      lastStatus = await attempt(item);
      if (retryable(lastStatus)) {
        write(read().map(i => (i.id === item.id ? { ...i, tries: i.tries + 1 } : i)));
        break;
      }
      // Sent, or permanently refused (e.g. validation): either way it must not block the rest.
      write(read().filter(i => i.id !== item.id));
      if (lastStatus >= 300) console.warn("outbox: dropped rejected write", item, lastStatus);
    }
    flushing = null;
    return { left: read().length, lastStatus };
  })();
  return flushing;
}

export function useOutbox() {
  const [items, setItems] = useState(read);
  const [online, setOnline] = useState(navigator.onLine);
  const [authNeeded, setAuthNeeded] = useState(false);

  useEffect(() => {
    const sync = () => setItems(read());
    const tryFlush = async () => {
      if (!read().length) return;
      const { lastStatus } = await flush();
      setAuthNeeded(lastStatus === 401);
    };
    const goOnline = () => { setOnline(true); tryFlush(); };
    const goOffline = () => setOnline(false);
    const onVisible = () => { if (document.visibilityState === "visible") tryFlush(); };
    window.addEventListener(EVENT, sync);
    window.addEventListener("storage", sync);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    document.addEventListener("visibilitychange", onVisible);
    const timer = setInterval(tryFlush, RETRY_MS);
    tryFlush();
    return () => {
      window.removeEventListener(EVENT, sync);
      window.removeEventListener("storage", sync);
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(timer);
    };
  }, []);

  return { items, count: items.length, online, authNeeded, flushNow: () => flush().then(r => setAuthNeeded(r.lastStatus === 401)) };
}

// Cached GET for reference data (sites, materials, today's list) so screens open offline.
export async function cachedJSON(url, cacheKey) {
  const key = "logsite_cache:" + (cacheKey || url);
  try {
    const r = await fetch(url, { credentials: "include" });
    if (!r.ok) throw new Error(String(r.status));
    const data = await r.json();
    try { localStorage.setItem(key, JSON.stringify(data)); } catch {}
    return data;
  } catch (e) {
    try {
      const hit = localStorage.getItem(key);
      if (hit) return JSON.parse(hit);
    } catch {}
    throw e;
  }
}
